import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import { FeaturePageStructuredData } from "../../config/StructuredData";

const rows = [
  { feature: "Client-wise work organisation", generic: "Projects or boards, not client-aware", simplifyte: "Every work item belongs to a named client" },
  { feature: "FY and period tracking", generic: "Custom labels or workarounds", simplifyte: "Built-in FY, quarter, and month fields" },
  { feature: "Maker-checker review workflow", generic: "Comments or status columns", simplifyte: "Structured review and approval stages with log" },
  { feature: "Document collection from clients", generic: "Not built in — attach files manually", simplifyte: "Document checklist per work item with reminders" },
  { feature: "Recurring compliance work", generic: "Duplicate tasks manually each period", simplifyte: "Period-based recurring work templates" },
  { feature: "Audit trail for professional use", generic: "Activity log (if available, often limited)", simplifyte: "Immutable log of every action with timestamp" },
  { feature: "Closure proof", generic: "Mark complete — no formal close step", simplifyte: "Formal closure with attachment and sign-off" },
  { feature: "Partner oversight dashboard", generic: "Custom reports or views required", simplifyte: "Partner dashboard for all open client work" },
  { feature: "CA firm workflow model", generic: "Generic — requires significant customisation", simplifyte: "Built for the CA firm workflow out of the box" },
  { feature: "Onboarding time", generic: "Weeks of setup and training", simplifyte: "Go live in days with guided onboarding" },
];

const GenericTaskToolsVsSimplifyte = () => (
  <>
    <FeaturePageStructuredData
      name="Generic Task Tools vs Simplifyte for CA Firms"
      description="Compare Trello, Asana, Monday, Notion vs Simplifyte for CA firms and professional service teams. See why generic project tools don't work for compliance workflow management."
      url="https://www.simplifyte.com/compare/generic-task-tools-vs-simplifyte"
    />
    <HeaderMenu />
    <main style={{ paddingTop: "80px", minHeight: "100vh" }}>
      <section style={{ background: "linear-gradient(135deg,#093A5B 0%,#0a4d6e 100%)", padding: "60px 24px", color: "#fff", textAlign: "center" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <p style={{ fontSize: "13px", letterSpacing: "2px", textTransform: "uppercase", opacity: 0.7, marginBottom: "16px" }}>Compare</p>
          <h1 style={{ fontSize: "clamp(26px,4vw,44px)", fontWeight: 700, lineHeight: 1.2, marginBottom: "20px" }}>Generic Task Tools vs Simplifyte for CA Firms</h1>
          <p style={{ fontSize: "17px", opacity: 0.85, maxWidth: "640px", margin: "0 auto 32px" }}>Trello, Asana, Monday, Notion — these tools are built for product teams and agencies, not professional service compliance workflows. See why CA firms choose Simplifyte.</p>
          <Link to="/contactsales" style={{ background: "#fff", color: "#093A5B", padding: "12px 28px", borderRadius: "8px", fontWeight: 600, textDecoration: "none" }}>See Simplifyte in Action</Link>
        </div>
      </section>

      <section style={{ padding: "60px 24px" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "26px", fontWeight: 700, color: "#093A5B", marginBottom: "16px" }}>Why generic project management tools fail for CA firms</h2>
          <p style={{ fontSize: "16px", color: "#475569", lineHeight: 1.7, marginBottom: "16px" }}>
            Generic task tools like Trello, Asana, Monday.com, and Notion are built for product managers and creative agencies. They organise work around projects, boards, and sprints — not around clients, financial years, compliance periods, and review workflows.
          </p>
          <p style={{ fontSize: "16px", color: "#475569", lineHeight: 1.7, marginBottom: "32px" }}>
            CA firms and professional service teams spend weeks trying to customise these tools to fit their workflow — and still end up with workarounds for period tracking, document collection, maker-checker approval, and closure proof. Simplifyte has all of this built in from day one.
          </p>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ background: "#093A5B", color: "#fff" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600 }}>Feature</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600 }}>Generic Tools (Trello / Asana / Notion)</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 600 }}>Simplifyte</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#f8fafc" : "#fff", borderBottom: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "12px 16px", fontWeight: 500, color: "#1e293b" }}>{row.feature}</td>
                    <td style={{ padding: "12px 16px", color: "#ef4444" }}>✗ {row.generic}</td>
                    <td style={{ padding: "12px 16px", color: "#16a34a" }}>✓ {row.simplifyte}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section style={{ padding: "40px 24px", background: "#f8fafc" }}>
        <div style={{ maxWidth: "820px", margin: "0 auto" }}>
          <h2 style={{ fontSize: "22px", fontWeight: 700, color: "#093A5B", marginBottom: "16px" }}>The real cost of using generic tools in a CA firm</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: "16px" }}>
            {[
              { title: "Setup time wasted", desc: "Every firm customises the tool differently. When a team member leaves, the setup goes with them." },
              { title: "No professional compliance model", desc: "Compliance work has a specific structure: prepare, review, approve, close. Generic tools don't enforce this." },
              { title: "No FY/period awareness", desc: "You can't filter 'all pending ITR work for AY 2025-26' in Trello without significant custom setup." },
              { title: "No built-in document workflow", desc: "Document requests to clients require external tools, emails, or WhatsApp — generic tools don't handle this." },
            ].map((item, i) => (
              <div key={i} style={{ background: "#fff", padding: "18px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#1e293b", marginBottom: "8px" }}>{item.title}</h3>
                <p style={{ fontSize: "14px", color: "#64748b", lineHeight: 1.6 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: "60px 24px", textAlign: "center", background: "#093A5B", color: "#fff" }}>
        <h2 style={{ fontSize: "26px", fontWeight: 700, marginBottom: "14px" }}>Switch from generic tools to software built for your workflow</h2>
        <p style={{ opacity: 0.8, marginBottom: "24px", fontSize: "16px" }}>Book a 20-minute demo — no configuration required, no customisation needed.</p>
        <Link to="/contactsales" style={{ background: "#fff", color: "#093A5B", padding: "13px 30px", borderRadius: "8px", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "8px" }}>Book a Demo <ArrowRight size={16} /></Link>
      </section>
    </main>
    <CustomFooter />
  </>
);

export default GenericTaskToolsVsSimplifyte;
